import { ContentLoader } from "@/components/ui/loaders";
import { http } from "@/lib/http";
import type { AuthData } from "@/types/auth-data";
import { AxiosError } from "axios";
import { createContext, useContext, useEffect, useState } from "react";
import { useToast } from "./toast.provider";

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  signIn: (data: AuthData) => Promise<void>;
  signUp: (data: RegisterSchema) => void;
  signOut: () => Promise<void>;
};

import { queryClient } from "@/lib/query-client";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { RegisterSchema } from "@/api/schemas/auth.schema";

export type User = {
  id: string;
  name: string;
  email: string;
  role: "ADMIN" | "USER";
  /** Senha redefinida pelo ADMIN: o app só libera depois da troca. */
  needToChangePassword: boolean;
};

const useMe = () => {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await http.get<User>("auth/me");
      return data;
    },
    retry: false,
  });
};

const useSignUp = () => {
  return useMutation({
    mutationFn: async (data: RegisterSchema) => {
      await http.post<{ user: User }>("auth/create-user", data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["me"],
      });
    },
  });
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { toaster } = useToast();
  const { data: user, isLoading: isLoadingMe } = useMe();
  const { mutate: signUp, error } = useSignUp();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (error) {
      toaster(error.message, "error");
    }
  }, [error]);

  const signIn = async (data: AuthData) => {
    setIsLoading(true);
    await http
      .post<{ success: true; accessToken: string }>("auth/sign-in", data)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["me"],
        });
        toaster("Login realizado com sucesso!", "success");
      })
      .catch((e: AxiosError<{ success: boolean; message: string }>) => {
        toaster(e?.message! || "Erro ao realizar login", "error");
      })
      .finally(() => setIsLoading(false));
  };

  const signOut = async () => {
    setIsLoading(true);
    await http
      .post<{ success: true }>("auth/sign-out")
      .then(() => {
        queryClient.setQueryData(["me"], null);
        queryClient.removeQueries();
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading: isLoading || isLoadingMe,
        signIn,
        signUp,
        signOut,
      }}
    >
      <ContentLoader show={isLoading} />
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}
